import * as React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { motion } from "framer-motion";
import { useTheme, Box, Tooltip, IconButton } from "@mui/material";
import { tokens } from "../../theme";
import CancelScheduleSendIcon from "@mui/icons-material/CancelScheduleSend";
import Axios from "axios";
import Alert from "../../components/alert/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import MoreInfoModal from "../students/moreInfo/MoreInfoModal";
import AddMemoDatePicker from "../students/moreInfo/AddMemoDatePicker";
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import dayjs from "dayjs";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import Divider from "@mui/material/Divider";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import ArticleIcon from '@mui/icons-material/Article';
import ParentPortalButton from '../../components/ParentPortalButton';
import StudentInfoCardModal from "./StudentInfoCardModal";
import getCookie from "../../utils/getCookies";
import MainMemoOptionsToolbar from "../weekly/mainMemo/MainMemoOptionsToolbar";
import stripHtml from "../../utils/stripHtml";
import sanitizeMemoHtml from "../../utils/sanitizeMemoHtml";
import MemoContent from "../../components/MemoContent";
import MemoCallButton from "../../voice/MemoCallButton";

dayjs.extend(timezone);
dayjs.extend(utc);

export default function ConsultationCard({
  student,
  setStudentUpdated,
  savePrevActions,
  setBeingModified,
  updatePotentialStudentsInfo,
  updateMemo,
  currentUser,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Rich-text memo editor refs (contentEditable body + reused weekly toolbar).
  // memoRef (defined below via useOuterClick) wraps the toolbar + editor so that
  // clicking a toolbar button does NOT count as an outside-click; the actual
  // HTML is read from these content refs, not from the wrapper.
  const memoContentRef = React.useRef(null);
  const memoToolbarRef = React.useRef(null);
  const newMemoContentRef = React.useRef(null);
  const newMemoToolbarRef = React.useRef(null);

  // for alert
  const [successAlertOpen, setSuccessAlertOpen] = React.useState(false);
  const [successAlertMessage, setSuccessAlertMessage] = React.useState("");
  const [errorAlertOpen, setErrorAlertOpen] = React.useState(false);
  const [errorAlertMessage, setErrorAlertMessage] = React.useState("");

  // reward code redeemed status
  const [rewardCodeRedeemed, setRewardCodeRedeemed] = React.useState(null);
  React.useEffect(() => {
    if (student.reward_code) {
      Axios.get(`${process.env.REACT_APP_URL}/api/v1/reward_codes/${encodeURIComponent(student.reward_code)}/status`, {
        headers: { Authorization: `Bearer ${getCookie('_auth')}` },
      }).then((res) => {
        setRewardCodeRedeemed(res.data.is_redeemed || false);
      }).catch(() => {});
    }
  }, [student.reward_code]);

  // for dialog
  const handleClickDialogMemoOpen = (memoId) => {
    setDialogMemoId(memoId);
  };

  const handleDialogMemoClose = () => {
    setDialogMemoId(null);
    setRollbackTask(null);
  };

  // more and add memo button toggle
  const [moreMemos, setMoreMemos] = React.useState(false);
  
  // bookmark filter state
  const [showBookmarkedOnly, setShowBookmarkedOnly] = React.useState(false);
  
  // Auto-expand memos when showing bookmarked only
  React.useEffect(() => {
    if (showBookmarkedOnly && student.memos?.some(m => m.bookmark)) {
      setMoreMemos(true);
    }
  }, [showBookmarkedOnly, student.memos]);

  // new memo info
  const [newMemoTitle, setNewMemoTitle] = React.useState("");
  const [newMemoContent, setNewMemoContent] = React.useState("");
  const [newMemoReminder, setNewMemoReminder] = React.useState(null);
  const [newMemoBookmark, setNewMemoBookmark] = React.useState(false);
  // Only mount the (heavy) new-memo formatting toolbar once the editor is engaged,
  // so a page full of cards doesn't render N toolbars up front.
  const [isAddingNewMemo, setIsAddingNewMemo] = React.useState(false);

  // for modify info

  const [modifyingParentName, setModifyingParentName] = React.useState(false);
  const [finishingModifyingParentName, setFinishingModifyingParentName] =
    React.useState(false);
  const [modifiedParentName, setModifiedParentName] = React.useState(
    student.parent_name ?? ""
  );

  const [modifyingPhone, setModifyingPhone] = React.useState(false);
  const [finishingModifyingPhone, setFinishingModifyingPhone] =
    React.useState(false);
  const [modifiedPhone, setModifiedPhone] = React.useState(
    student.parent_phone ?? ""
  );

  const [modifyingEmail, setModifyingEmail] = React.useState(false);
  const [finishingModifyingEmail, setFinishingModifyingEmail] =
    React.useState(false);
  const [modifiedEmail, setModifiedEmail] = React.useState(
    student.parent_email ?? ""
  );

  const [modifyingEmail2, setModifyingEmail2] = React.useState(false);
  const [finishingModifyingEmail2, setFinishingModifyingEmail2] =
    React.useState(false);
  const [modifiedEmail2, setModifiedEmail2] = React.useState(
    student.parent_email_2 ?? ""
  );

  // for memo, using memo id to modify
  const [modifyingMemo, setModifyingMemo] = React.useState(-1);
  const [finishingModifyingMemo, setFinishingModifyingMemo] = React.useState([
    -1,
    false,
  ]);
  const [modifiedMemo, setModifiedMemo] = React.useState(
    student.memos ? memoMap(student.memos) : new Map()
  );


  // for memo title
  const [modifyingMemoTitle, setModifyingMemoTitle] = React.useState(-1);
  const [finishingModifyingMemoTitle, setFinishingModifyingMemoTitle] =
    React.useState([-1, false]);

  //for task handling with memos
  const [dialogMemoId, setDialogMemoId] = React.useState(null);

  const [completeDialogOpen, setCompleteDialogOpen] = React.useState(false);

  const [rollbackTask, setRollbackTask] = React.useState(null);

  const handleBookmarkToggle = async (memoId, currentBookmarkStatus) => {
    try {
      await Axios.put(
        `${process.env.REACT_APP_URL}/api/v1/memos/${memoId}/bookmark`,
        { bookmark: !currentBookmarkStatus },
        {
          headers: {
            Authorization: `Bearer ${getCookie('_auth')}`,
          },
        }
      );
      setStudentUpdated(true);
      setSuccessAlertMessage(
        currentBookmarkStatus ? "Bookmark removed" : "Bookmark added"
      );
      setSuccessAlertOpen(true);
    } catch (error) {
      console.error('Failed to update bookmark:', error);
      setErrorAlertMessage(
        error.response?.data?.message || "Failed to update bookmark"
      );
      setErrorAlertOpen(true);
    }
  };

  function memoMap(memos) {
    const memoMap = new Map();
    memos.forEach((memo) => {
      memoMap.set(memo.id, memo);
    });

    return memoMap;
  }



  React.useEffect(() => {
    // if (!modifyingConsulDate)
    //   setModifiedConsulDate(student.consultation_date ?? "");
    // if (!modifyingDateEmailSent) {
    //   setModifiedDateEmailSent(
    //     student.info_mail !== undefined &&
    //       student.info_mail !== null &&
    //       student.info_mail.date !== null &&
    //       student.info_mail.date !== ""
    //       ? dayjs(student.info_mail.date)
    //       : null
    //   );
    // }
    if (!modifyingParentName) setModifiedParentName(student.parent_name ?? "");
    if (!modifyingPhone) setModifiedPhone(student.parent_phone ?? "");
    if (!modifyingEmail) setModifiedEmail(student.parent_email ?? "");
    if (!modifyingEmail2) setModifiedEmail2(student.parent_email_2 ?? "");
    if (modifyingMemo === -1)
      setModifiedMemo(student.memos ? memoMap(student.memos) : new Map());
  }, [student]);


  // When a memo enters edit mode, load its HTML into the contentEditable and focus it.
  React.useEffect(() => {
    if (modifyingMemo > -1 && memoContentRef.current) {
      const html = sanitizeMemoHtml(modifiedMemo.get(modifyingMemo)?.content);
      if (memoContentRef.current.innerHTML !== html) {
        memoContentRef.current.innerHTML = html;
      }
      memoContentRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifyingMemo]);

  const uploadNewMemo = async (event) => {
    event.preventDefault();

    const newContentHtml = newMemoContentRef.current?.innerHTML || "";

    if (newMemoTitle === "" && stripHtml(newContentHtml).trim() === "") {
      setErrorAlertMessage("Please enter a title or content for the new memo.");
      setErrorAlertOpen(true);
      return;
    }

    await Axios.post(
      `${process.env.REACT_APP_URL}/api/v1/students/${student.uuid}/memos`,
      {
        title: newMemoTitle,
        content: newContentHtml,
        reminder: newMemoReminder,
        bookmark: newMemoBookmark,
      },
      {
        headers: {
          Authorization: `Bearer ${
getCookie('_auth')
          }`,
        },
      }
    )
      .then((response) => {
        setSuccessAlertMessage("New memo has been added!");
        setSuccessAlertOpen(true);
        setStudentUpdated(true);
      })
      .catch((error) => {
        console.log(error);
        setErrorAlertMessage(error.response?.data?.message || "Something went wrong");
        setErrorAlertOpen(true);
      });

    setNewMemoTitle("");
    setNewMemoContent("");
    if (newMemoContentRef.current) newMemoContentRef.current.innerHTML = "";
    setIsAddingNewMemo(false);
    setNewMemoReminder(null);
    setNewMemoBookmark(false);
  };


  function useOuterClick(callback) {
    const innerRef = React.useRef();
    const callbackRef = React.useRef();


    // set current callback in ref, before second useEffect uses it
    React.useEffect(() => {
      // useEffect wrapper to be safe for concurrent mode
      callbackRef.current = callback;
    });

    React.useEffect(() => {
      document.addEventListener("click", handleClick);
      return () => document.removeEventListener("click", handleClick);

      // read most recent callback and innerRef dom node from refs
      function handleClick(e) {
        if (
          innerRef.current &&
          callbackRef.current &&
          !innerRef.current.contains(e.target)
        ) {
          callbackRef.current(e);
        }
      }
    }, []); // no need for callback + innerRef dep

    return innerRef; // return ref; client can omit `useRef`
  }


  const parentNameRef = useOuterClick((e) => {
    // if other input is being modified, return
    if (
      modifyingPhone ||
      modifyingEmail ||
      modifyingEmail2 ||
      modifyingMemo > -1 ||
      modifyingMemoTitle > -1
    )
      return;

    if (!finishingModifyingParentName) {
      setFinishingModifyingParentName(true);
      parentNameRef.current.focus();
      setBeingModified(true);
      return;
    }

    if (
      parentNameRef.current.value === student.parent_name ||
      (parentNameRef.current.value === "" && student.parent_name === null)
    ) {
      console.log("No change");
      setModifyingParentName(false);
      setFinishingModifyingParentName(false);
      setBeingModified(false);
      return;
    }

    // need to update the parent name
    console.log("Change");
    setModifyingParentName(false);
    setFinishingModifyingParentName(false);
    savePrevActions(
      student.uuid,
      null,
      "parent_name",
      student.parent_name, // prev value
      parentNameRef.current.value, // new value
      student.consultation_number
    );
    updatePotentialStudentsInfo(student.uuid, {
      ...student,
      parent_name: parentNameRef.current.value,
    });
    student.parent_name = parentNameRef.current.value;
    setBeingModified(false);
  });

  const phoneInnerRef = useOuterClick((e) => {
    // if other input is being modified, return
    if (
      modifyingParentName ||
      modifyingEmail ||
      modifyingEmail2 ||
      modifyingMemo > -1 ||
      modifyingMemoTitle > -1
    )
      return;
    if (!finishingModifyingPhone) {
      setFinishingModifyingPhone(true);
      phoneInnerRef.current.focus();
      setBeingModified(true);
      return;
    }
    if (
      phoneInnerRef.current.value === student.parent_phone ||
      (phoneInnerRef.current.value === "" && student.parent_phone === null)
    ) {
      console.log("No change");
      setModifyingPhone(false);
      setFinishingModifyingPhone(false);
      setBeingModified(false);
      return;
    }
    // need to update the phone number
    console.log("Change");
    setModifyingPhone(false);
    setFinishingModifyingPhone(false);
    savePrevActions(
      student.uuid,
      null,
      "parent_phone",
      student.parent_phone, // prev value
      phoneInnerRef.current.value, // new value
      student.consultation_number
    );
    updatePotentialStudentsInfo(student.uuid, {
      ...student,
      parent_phone: phoneInnerRef.current.value,
    });
    student.parent_phone = phoneInnerRef.current.value;
    setBeingModified(false);
  });

  const emailInnerRef = useOuterClick((e) => {
    // if other input is being modified, return
    if (
      modifyingParentName ||
      modifyingPhone ||
      modifyingEmail2 ||
      modifyingMemo > -1 ||
      modifyingMemoTitle > -1
    )
      return;

    if (!finishingModifyingEmail) {
      setFinishingModifyingEmail(true);
      emailInnerRef.current.focus();
      setBeingModified(true);
      return;
    }

    if (
      emailInnerRef.current.value === student.parent_email ||
      (emailInnerRef.current.value === "" && student.parent_email === null)
    ) {
      console.log("No change");
      setModifyingEmail(false);
      setFinishingModifyingEmail(false);
      setBeingModified(false);
      return;
    }

    // need to update the email
    console.log("Change");
    setModifyingEmail(false);
    setFinishingModifyingEmail(false);
    savePrevActions(
      student.uuid,
      null,
      "parent_email",
      student.parent_email, // prev value
      emailInnerRef.current.value, // new value
      student.consultation_number
    );
    updatePotentialStudentsInfo(student.uuid, {
      ...student,
      parent_email: emailInnerRef.current.value,
    });
    student.parent_email = emailInnerRef.current.value;
    setBeingModified(false);
  });

  const email2InnerRef = useOuterClick((e) => {
    // if other input is being modified, return
    if (
      modifyingParentName ||
      modifyingPhone ||
      modifyingEmail ||
      modifyingMemo > -1 ||
      modifyingMemoTitle > -1
    )
      return;

    if (!finishingModifyingEmail2) {
      setFinishingModifyingEmail2(true);
      email2InnerRef.current.focus();
      setBeingModified(true);
      return;
    }

    if (
      email2InnerRef.current.value === student.parent_email_2 ||
      (email2InnerRef.current.value === "" && student.parent_email_2 === null)
    ) {
      console.log("No change");
      setModifyingEmail2(false);
      setFinishingModifyingEmail2(false);
      setBeingModified(false);
      return;
    }

    // need to update the email
    console.log("Change");
    setModifyingEmail2(false);
    setFinishingModifyingEmail2(false);
    savePrevActions(
      student.uuid,
      null,
      "parent_email_2",
      student.parent_email_2, // prev value
      email2InnerRef.current.value, // new value
      student.consultation_number
    );
    updatePotentialStudentsInfo(student.uuid, {
      ...student,
      parent_email_2: email2InnerRef.current.value,
    });
    student.parent_email_2 = email2InnerRef.current.value;
    setBeingModified(false);
  });

  const memoRef = useOuterClick((e) => {
    // if other input is being modified, return
    if (
      modifyingParentName ||
      modifyingPhone ||
      modifyingEmail || 
      modifyingEmail2 ||
      modifyingMemo !== parseInt(memoRef.current.getAttribute("data-key")) ||
      modifyingMemoTitle > -1
    )
      return;

    if (
      finishingModifyingMemo[0] !==
        parseInt(memoRef.current.getAttribute("data-key")) ||
      (finishingModifyingMemo[0] ===
        parseInt(memoRef.current.getAttribute("data-key")) &&
        !finishingModifyingMemo[1])
    ) {
      setFinishingModifyingMemo([
        parseInt(memoRef.current.getAttribute("data-key")),
        true,
      ]);
      memoContentRef.current?.focus();
      setBeingModified(true);
      return;
    }

    const memo =
      student.memos.filter(
        (memo) => memo.id === parseInt(memoRef.current.getAttribute("data-key"))
      )[0] ?? null;

    if (
      memo !== null &&
      (memoContentRef.current?.innerHTML === memo.content ||
        (!memoContentRef.current?.innerHTML && memo?.content === null))
    ) {
      console.log("No change");
      setModifyingMemo(-1);
      setFinishingModifyingMemo([-1, false]);
      setBeingModified(false);
      return;
    }

    // need to update the memo
    console.log("Change");
    setModifyingMemo(-1);
    setFinishingModifyingMemo([-1, false]);
    savePrevActions(
      student.uuid,
      memo.id,
      "memo_content",
      memo.content, // prev value
      memoContentRef.current?.innerHTML, // new value
      student.consultation_number
    );
    updateMemo(student.uuid, memo.id, {
      ...memo,
      content: memoContentRef.current?.innerHTML,
    });
    memo.content = memoContentRef.current?.innerHTML;
    setBeingModified(false);
  });

  const memoTitleRef = useOuterClick((e) => {
    // if other input is being modified, return
    if (
      modifyingParentName ||
      modifyingPhone ||
      modifyingEmail ||
      modifyingEmail2 ||
      modifyingMemo > -1 ||
      modifyingMemoTitle !==
        parseInt(memoTitleRef.current.getAttribute("data-key"))
    )
      return;

    if (
      finishingModifyingMemoTitle[0] !==
        parseInt(memoTitleRef.current.getAttribute("data-key")) ||
      (finishingModifyingMemoTitle[0] ===
        parseInt(memoTitleRef.current.getAttribute("data-key")) &&
        !finishingModifyingMemoTitle[1])
    ) {
      setFinishingModifyingMemoTitle([
        parseInt(memoTitleRef.current.getAttribute("data-key")),
        true,
      ]);
      memoTitleRef.current.focus();
      setBeingModified(true);
      return;
    }

    const memo =
      student.memos.filter(
        (memo) =>
          memo.id === parseInt(memoTitleRef.current.getAttribute("data-key"))
      )[0] ?? null;

    if (
      memo !== null &&
      (memoTitleRef.current.value === memo.title ||
        (memoTitleRef.current.value === "" && memo?.title === null))
    ) {
      console.log("No change");
      setModifyingMemoTitle(-1);
      setFinishingModifyingMemoTitle([-1, false]);
      setBeingModified(false);
      return;
    }

    // need to update the memo title
    console.log("Change");
    setModifyingMemoTitle(-1);
    setFinishingModifyingMemoTitle([-1, false]);
    savePrevActions(
      student.uuid,
      memo.id,
      "memo_title",
      memo.title, // prev value
      memoTitleRef.current.value, // new value
      student.consultation_number
    );
    updateMemo(student.uuid, memo.id, {
      ...memo,
      title: memoTitleRef.current.value,
    });
    memo.title = memoTitleRef.current.value;
    setBeingModified(false);
  });

  function getDayInKorean(date) {
    const day = dayjs(date).day();
    switch (day) {
      case 0:
        return "일";
      case 1:
        return "월";
      case 2:
        return "화";
      case 3:
        return "수";
      case 4:
        return "목";
      case 5:
        return "금";
      case 6:
        return "토";
      default:
        return "";
    }
  }

  function calculateFirstEmailSentAt(requested_at) {
    const estTime = dayjs(requested_at)
      .tz("America/New_York")
      .hour(22)
      .minute(30)
      .second(0);
    return estTime.format("YYYY-MM-DD HH:mm");
  }

  function calculateSecondEmailSentAt(requested_at) {
    const estTime = dayjs(requested_at)
      .tz("America/New_York")
      .add(7, "day")
      .hour(22)
      .minute(30)
      .second(0);
    return estTime.format("YYYY-MM-DD HH:mm");
  }

  return (
    <motion.div
      animate={{ opacity: 1 }}
      initial={{ opacity: 0 }}
      exit={{ opacity: 0 }}
      layout
    >
      <Card
        sx={{
          position: "relative",
          minWidth: 600,
          maxWidth: 600,
          overflow: "visible", // allow the built-in memo toolbar's color-picker popups to escape the card
          backgroundColor: colors.primary[600],
          // boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.5)",
          border: `1px solid ${colors.primary[600]}`,
          // ":hover": {
          //     // boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.5)",
          //     border: "1px solid grey",
          // }
        }}
        key={student.id}
      >
        <CardContent
          sx={{
            paddingX: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              paddingX: "10px",
              position: "relative",
              minHeight: "50px", // Match the sidebar header height
              marginBottom: "40px", // Space between sections
              marginTop: "5px", // Match the sidebar's paddingTop
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 0.5,
                height: "50px", // Match sidebar header height
                zIndex: 1,
              }}
            >
              <Typography variant="h3">
                {student.email_code ? `(${student.email_code})` : ''} {student.consultation_number ? student.consultation_number : 'N/A'}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center", // Changed to center
                gap: 1,
                width: "100%",
                justifyContent: "flex-end",
                height: "50px", // Match sidebar header height
              }}
            >
              {student.is_potential_student &&
                student.info_mail &&
                !student.info_mail.is_sent &&
                student.memos.some((memo) =>
                  memo.tasks.some((task) => task.is_info_mail === true)
                ) && (
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}
                  >
                    {student.memos.map((memo, memoIndex) =>
                      memo.tasks?.map(
                        (task, taskIndex) =>
                          task.is_info_mail && (
                            <Box
                              key={`${memoIndex}-${taskIndex}`}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                minHeight: "72px",
                              }}
                            >
                              <Tooltip title="신규문의">
                                <CancelScheduleSendIcon
                                  fontSize="small"
                                  color="error"
                                  sx={{ cursor: "pointer" }}
                                  onClick={() => {
                                    if (!moreMemos) {
                                      setMoreMemos(true);
                                      setTimeout(() => {
                                        const memoElement =
                                          document.getElementById(
                                            `memo-${memo.id}`
                                          );
                                        if (memoElement) {
                                          memoElement.scrollIntoView({
                                            behavior: "smooth",
                                            block: "center",
                                          });
                                        } else {
                                          console.log(
                                            `Could not find memo element with id: memo-${memo.id}`
                                          );
                                        }
                                      }, 300);
                                    } else {
                                      const memoElement =
                                        document.getElementById(
                                          `memo-${memo.id}`
                                        );
                                      if (memoElement) {
                                        memoElement.scrollIntoView({
                                          behavior: "smooth",
                                          block: "center",
                                        });
                                      } else {
                                        console.log(
                                          `Could not find memo element with id: memo-${memo.id}`
                                        );
                                      }
                                    }
                                  }}
                                />
                              </Tooltip>
                              {task.first_email_sent_at && (
                                <Tooltip title="First email sent">
                                  <CancelScheduleSendIcon
                                    fontSize="small"
                                    sx={{
                                      color: colors.grey[500],
                                      marginTop: "2px",
                                    }}
                                  />
                                </Tooltip>
                              )}
                              {task.second_email_sent_at && (
                                <Tooltip title="Second email sent">
                                  <CancelScheduleSendIcon
                                    fontSize="small"
                                    sx={{
                                      color: colors.blueAccent[700],
                                      marginTop: "2px",
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          )
                      )
                    )}
                  </Box>
                )}

              {student.memos &&
                student.memos.some((memo) => memo.tasks?.length > 0) &&
                student.memos.some((memo) =>
                  memo.tasks.some((task) => task.is_info_mail === false && task.is_completed === false)
                ) && (
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}
                  >
                    {student.memos.map((memo, memoIndex) =>
                      memo.tasks?.map(
                        (task, taskIndex) =>
                          !task.is_info_mail && !task.is_completed && (
                            <Box
                              key={`${memoIndex}-${taskIndex}`}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                minHeight: "72px",
                              }}
                            >
                              <Tooltip title="재문의">
                                <CancelScheduleSendIcon
                                  fontSize="small"
                                  color={
                                    task.is_info_mail ? "error" : "warning"
                                  }
                                  sx={{ cursor: "pointer" }}
                                  onClick={() => {
                                    if (!moreMemos) {
                                      setMoreMemos(true);
                                      setTimeout(() => {
                                        const memoElement =
                                          document.getElementById(
                                            `memo-${memo.id}`
                                          );
                                        if (memoElement) {
                                          memoElement.scrollIntoView({
                                            behavior: "smooth",
                                            block: "center",
                                          });
                                        } else {
                                          console.log(
                                            `Could not find memo element with id: memo-${memo.id}`
                                          );
                                        }
                                      }, 300);
                                    } else {
                                      const memoElement =
                                        document.getElementById(
                                          `memo-${memo.id}`
                                        );
                                      if (memoElement) {
                                        memoElement.scrollIntoView({
                                          behavior: "smooth",
                                          block: "center",
                                        });
                                      } else {
                                        console.log(
                                          `Could not find memo element with id: memo-${memo.id}`
                                        );
                                      }
                                    }
                                  }}
                                />
                              </Tooltip>
                              {task.first_email_sent_at && (
                                <Tooltip title="First email sent">
                                  <CancelScheduleSendIcon
                                    fontSize="small"
                                    sx={{
                                      color: colors.grey[500],
                                      marginTop: "2px",
                                    }}
                                  />
                                </Tooltip>
                              )}
                              {task.second_email_sent_at && (
                                <Tooltip title="Second email sent">
                                  <CancelScheduleSendIcon
                                    fontSize="small"
                                    sx={{
                                      color: colors.blueAccent[700],
                                      marginTop: "2px",
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          )
                      )
                    )}
                  </Box>
                )}
            </Box>
            {student.reward_code && (
              <Tooltip
                title={`Reward Code: ${student.reward_code}${rewardCodeRedeemed === true ? " (Redeemed)" : rewardCodeRedeemed === false ? " (Not Redeemed)" : ""}`}
                sx={{ height: "50px", display: "flex", alignItems: "center" }}
              >
                <CardGiftcardIcon sx={{ color: rewardCodeRedeemed ? "grey" : "green" }} />
              </Tooltip>
            )}
            <Tooltip
              title="Blacklist"
              sx={{ height: "50px", display: "flex", alignItems: "center" }}
            >
              {student.is_blacklist_student ? <PersonOffIcon /> : <></>}
            </Tooltip>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              paddingX: "10px",
            }}
          >
            <MoreInfoModal
              student={student}
              setStudentUpdated={setStudentUpdated}
            />
            <ParentPortalButton
              parentEmail={student.parent_email}
              consultationNumber={student.consultation_number}
              studentUuid={student.uuid}
              sx={{
                backgroundColor: colors.greenAccent[700],
                color: colors.primary[100],
                "&:hover": {
                  backgroundColor: colors.greenAccent[600],
                },
              }}
            />
            <Box
                sx={{
                    display: "flex",
                    gap: 1,
                }}
            >
                {/* Student Info Card Generator */}
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                >
                    <StudentInfoCardModal
                        student_uuid={student.uuid}
                        size="small"
                        setErrorAlertMessage={setErrorAlertMessage}
                        setErrorAlertOpen={setErrorAlertOpen}
                        setSuccessAlertMessage={setSuccessAlertMessage}
                        setSuccessAlertOpen={setSuccessAlertOpen}
                    />
                </Box>
                <Button
                size="small"
                sx={{
                    backgroundColor: colors.blueAccent[900],
                    color: colors.primary[400],
                    "&:hover": {
                    backgroundColor: colors.blueAccent[800],
                    },
                }}
                onClick={() => {
                    // navigate(`/admin/potentialStudents/${student.uuid}/emailGenerator`);
                    window.open(
                    `/admin/consultationProgress/${student.uuid}/emailGenerator`,
                    "_blank"
                    );
                }}
                >
                Generate Email
                </Button>
            </Box>
          </Box>
          <br />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {/* Consultation Date */}
            {/* <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minHeight: 50,
                borderTop: `1px solid ${colors.grey[800]}`,
                borderBottom: `1px solid ${colors.grey[800]}`,
                textAlign: "center",
              }}
              onClick={() => {
                if (
                  modifyingDateEmailSent ||
                  modifyingParentName ||
                  modifyingPhone ||
                  modifyingEmail ||
                  modifyingMemo > -1 ||
                  modifyingMemoTitle > -1
                )
                  return;
                setModifyingConsulDate(true);
              }}
            >
              {modifyingConsulDate ? (
                <input
                  type="text"
                  value={modifiedConsulDate}
                  onChange={(e) => setModifiedConsulDate(e.target.value)}
                  ref={consulDateRef}
                  style={{
                    flex: 1,
                    width: "100%",
                    height: "100%",
                    textAlign: "center",
                  }}
                />
              ) : (
                <Typography
                  variant="h6"
                  color={
                    student.consultation_date ? "primary" : colors.grey[700]
                  }
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    flex: 1,
                  }}
                >
                  {modifiedConsulDate !== "" ? modifiedConsulDate : "N/A"}
                </Typography>
              )}
            </Box> */}
            {/* if they will register */}
            {/* <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minHeight: 50,
                borderBottom: `1px solid ${colors.grey[800]}`,
                textAlign: "center",
              }}
            ></Box> */}
            {/* Date email sent */}
            {/* <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minHeight: 70,
                borderBottom: `1px solid ${colors.grey[800]}`,
                textAlign: "center",
                alignItems: "center",
              }}
              onClick={() => {
                if (
                  modifyingConsulDate ||
                  modifyingParentName ||
                  modifyingPhone ||
                  modifyingEmail ||
                  modifyingMemo > -1 ||
                  modifyingMemoTitle > -1
                )
                  return;
                setModifyingDateEmailSent(true);
              }}
            >
              {modifyingDateEmailSent ? (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={["DatePicker"]}>
                    <DatePicker
                      label="Date"
                      value={modifiedDateEmailSent}
                      onChange={(newValue) => {
                        if (newValue === null || newValue === undefined) return;
                        setModifiedDateEmailSent(newValue);
                      }}
                      ref={dateEmailSendRef}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              ) : (
                <Typography
                  variant="h6"
                  color={student.info_mail?.date ? "primary" : colors.grey[700]}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    flex: 1,
                  }}
                >
                  {modifiedDateEmailSent !== null
                    ? dayjs(modifiedDateEmailSent).format("YYYY-MM-DD") +
                      " " +
                      getDayInKorean(modifiedDateEmailSent)
                    : "N/A"}
                </Typography>
              )}
            </Box> */}
            {/* Parent Name */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minHeight: 50,
                borderBottom: `1px solid ${colors.grey[800]}`,
                textAlign: "center",
              }}
              onClick={() => {
                if (
                  // modifyingConsulDate ||
                  // modifyingDateEmailSent ||
                  modifyingPhone ||
                  modifyingEmail ||
                  modifyingEmail2 ||
                  modifyingMemo > -1 ||
                  modifyingMemoTitle > -1
                )
                  return;
                setModifyingParentName(true);
              }}
            >
              {modifyingParentName ? (
                <input
                  type="text"
                  value={modifiedParentName}
                  onChange={(e) => setModifiedParentName(e.target.value)}
                  ref={parentNameRef}
                  style={{
                    flex: 1,
                    width: "100%",
                    height: "100%",
                    textAlign: "center",
                  }}
                />
              ) : (
                <Typography
                  variant="h6"
                  color={student.parent_name ? "primary" : colors.grey[700]}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    flex: 1,
                  }}
                >
                  {modifiedParentName !== "" ? modifiedParentName : "N/A"}
                </Typography>
              )}
            </Box>
            {/* Parent Phone */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minHeight: 50,
                borderBottom: `1px solid ${colors.grey[800]}`,
                textAlign: "center",
              }}
              onClick={() => {
                if (
                  // modifyingConsulDate ||
                  // modifyingDateEmailSent ||
                  modifyingParentName ||
                  modifyingPhone ||
                  modifyingEmail ||
                  modifyingEmail2 ||
                  modifyingMemo > -1 ||
                  modifyingMemoTitle > -1
                )
                  return;
                setModifyingPhone(true);
              }}
            >
              {modifyingPhone ? (
                <input
                  type="text"
                  value={modifiedPhone}
                  onChange={(e) => setModifiedPhone(e.target.value)}
                  ref={phoneInnerRef}
                  style={{
                    flex: 1,
                    width: "100%",
                    height: "100%",
                    textAlign: "center",
                  }}
                />
              ) : (
                <Typography
                  variant="h6"
                  color={student.parent_phone ? "primary" : colors.grey[700]}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    flex: 1,
                  }}
                >
                  {modifiedPhone !== "" ? modifiedPhone : "N/A"}
                </Typography>
              )}
            </Box>
            {/* Parent Email */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minHeight: 50,
                borderBottom: `1px solid ${colors.grey[800]}`,
                textAlign: "center",
              }}
              onClick={() => {
                if (
                  // modifyingConsulDate ||
                  // modifyingDateEmailSent ||
                  modifyingParentName ||
                  modifyingPhone ||
                  modifyingEmail ||
                  modifyingEmail2 ||
                  modifyingMemo > -1 ||
                  modifyingMemoTitle > -1
                )
                  return;
                setModifyingEmail(true);
              }}
            >
              {modifyingEmail ? (
                <input
                  type="text"
                  value={modifiedEmail}
                  onChange={(e) => setModifiedEmail(e.target.value)}
                  ref={emailInnerRef}
                  style={{
                    flex: 1,
                    width: "100%",
                    height: "100%",
                    textAlign: "center",
                  }}
                />
              ) : (
                <Typography
                  variant="h6"
                  color={student.parent_email ? "primary" : colors.grey[700]}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    flex: 1,
                  }}
                >
                  {modifiedEmail !== "" ? modifiedEmail : "N/A"}
                </Typography>
              )}
            </Box>
            {/* Parent Email 2 */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minHeight: 50,
                borderBottom: `1px solid ${colors.grey[800]}`,
                textAlign: "center",
              }}
              onClick={() => {
                if (
                  // modifyingConsulDate ||
                  // modifyingDateEmailSent ||
                  modifyingParentName ||
                  modifyingPhone ||
                  modifyingEmail ||
                  modifyingEmail2 ||
                  modifyingMemo > -1 ||
                  modifyingMemoTitle > -1
                )
                  return;
                setModifyingEmail2(true);
              }}
            >
              {modifyingEmail2 ? (
                <input
                  type="text"
                  value={modifiedEmail2}
                  onChange={(e) => setModifiedEmail2(e.target.value)}
                  ref={email2InnerRef}
                  style={{
                    flex: 1,
                    width: "100%",
                    height: "100%",
                    textAlign: "center",
                  }}
                />
              ) : (
                <Typography
                  variant="h6"
                  color={student.parent_email_2 ? "primary" : colors.grey[700]}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    flex: 1,
                  }}
                >
                  {modifiedEmail2 !== "" ? modifiedEmail2 : "N/A"}
                </Typography>
              )}
            </Box>
            {/* Student info files */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                minHeight: 50,
                borderBottom: `1px solid ${colors.grey[800]}`,                
                justifyContent: "space-evenly",
                alignContent: "center",
                flexWrap: "wrap",
              }}              
            >
              {student.student_info_files && student.student_info_files.length > 0 ? (
                  student.student_info_files.map((file, index) => (
                      <Box 
                          key={index} 
                          sx={{ 
                              display: "flex", 
                              alignItems: "center", 
                              gap: "0.5rem",
                              backgroundColor: "#f5f5f5",
                              padding: "0 4px",
                              borderRadius: "8px",
                          }}
                      >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                              <ArticleIcon />
                          </Box>
                          <Box
                              key={index}
                              component="a"
                              href={file.file_url}
                              target="_blank"
                              sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  textDecoration: "none",
                                  color: "inherit",
                              }}
                          >
                              <Typography variant="body1">{file.name}</Typography>
                          </Box>                                              
                      </Box>
                  ))
              ) : (
                <Typography
                    variant="h6"
                    color={colors.grey[700]}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      flex: 1,
                    }}
                  >
                    No files yet
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minHeight: "100%",
                textAlign: "center",
                padding: "0 20px",
              }}
            >
              {(() => {
                const filteredMemos = student.memos
                  ?.sort((a, b) => {
                    if (a.order === null && b.order === null) {
                      return a.createdAt > b.createdAt ? -1 : 1;
                    }
                    if (a.order === null) {
                      return -1;
                    }
                    if (b.order === null) {
                      return 1;
                    }
                    return a.order < b.order ? -1 : 1;
                  })
                  .filter((memo) => {
                    // Apply bookmark filter if enabled
                    if (showBookmarkedOnly) {
                      return memo.bookmark === true;
                    }
                    return true;
                  });

                // Show message if no bookmarked memos when filter is on
                if (showBookmarkedOnly && (!filteredMemos || filteredMemos.length === 0)) {
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "200px",
                        gap: 2,
                      }}
                    >
                      <BookmarkBorderIcon 
                        sx={{ 
                          fontSize: "48px", 
                          color: colors.grey[500] 
                        }} 
                      />
                      <Typography 
                        variant="h6" 
                        color={colors.grey[400]}
                      >
                        No bookmarked memos found
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => setShowBookmarkedOnly(false)}
                        sx={{
                          borderColor: colors.grey[500],
                          color: colors.grey[300],
                          "&:hover": {
                            borderColor: colors.grey[400],
                          }
                        }}
                      >
                        Show All Memos
                      </Button>
                    </Box>
                  );
                }

                return filteredMemos?.map((memo, index) => {
                  if (memo === undefined || memo === null) return null;

                  if (index === 0) {
                    return (
                      <Box
                        key={memo.id}
                        id={`memo-${memo.id}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          width: "100%",
                          minHeight: "400px",
                          textAlign: "center",
                          padding: "20px 0 40px 0",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 1,
                            width: "100%",
                            justifyContent: "flex-end",
                            alignItems: "center",
                          }}
                        >
                          {/* a memo written from a phone call opens the call (recording + full transcript) */}
                          <MemoCallButton memo={memo} size={18} />
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBookmarkToggle(memo.id, memo.bookmark);
                            }}
                            sx={{ 
                              padding: '4px',
                              '&:hover': {
                                backgroundColor: 'transparent',
                              }
                            }}
                          >
                            {memo.bookmark ? (
                              <BookmarkIcon
                                fontSize="small"
                                sx={{ 
                                  color: '#ed6c02',
                                  transition: 'color 0.2s ease',
                                  '&:hover': {
                                    color: '#ff8c00'
                                  }
                                }}
                              />
                            ) : (
                              <BookmarkBorderIcon
                                fontSize="small"
                                sx={{ 
                                  color: colors.grey[500],
                                  transition: 'color 0.2s ease',
                                  '&:hover': {
                                    color: colors.grey[300]
                                  }
                                }}
                              />
                            )}
                          </IconButton>
                          <Tooltip
                            title={
                              memo.tasks?.length > 0 && memo.tasks[0].claimed_by
                                ? `Assigned to: ${memo.tasks[0].claimed_by}`
                                : memo.tasks?.length > 0
                                ? "Unassigned"
                                : "No task created"
                            }
                          >
                            <CancelScheduleSendIcon
                              fontSize="small"
                              color={
                               (memo.tasks?.length > 0)
                                  ? memo.tasks[0].is_info_mail && !memo.tasks[0].is_completed
                                    ? "error"
                                    : !memo.tasks[0].is_info_mail && !memo.tasks[0].is_completed
                                      ? "warning"
                                      : "disabled"
                                  : "disabled"
                              }
                              sx={{ cursor: "pointer" }}
                              onClick={() => handleClickDialogMemoOpen(memo.id)}
                            />
                          </Tooltip>
                          <Dialog
                            open={dialogMemoId === memo.id}
                            onClose={handleDialogMemoClose}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                            PaperProps={{
                              style: {
                                minWidth: "500px",
                                padding: "20px",
                              },
                            }}
                          >
                            <DialogTitle id="alert-dialog-title">
                              <Typography
                                variant="h5"
                                color="primary"
                                sx={{
                                  marginBottom: "15px",
                                  fontWeight: "bold",
                                }}
                              >
                                {memo.tasks?.length > 0
                                  ? memo.tasks[0].is_info_mail
                                    ? `(신규문의) This memo is currently registered as a task.`
                                    : "(재문의) This memo is currently registered as a task."
                                  : "Would you like to create a new task for this memo?"}
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontStyle: "italic",
                                  opacity: 0.9,
                                }}
                              >
                                {memo.tasks?.length > 0 &&
                                  (memo.tasks[0].claimed_by
                                    ? `Assigned to: ${memo.tasks[0].claimed_by}`
                                    : `Assigned to: N/A`)}
                              </Typography>
                              <Divider />
                              {rollbackTask && (
                                <Box
                                  sx={{
                                    marginTop: "20px",
                                    padding: "15px",
                                    backgroundColor: colors.primary[900],
                                    borderRadius: "8px",
                                    border: `1px solid ${colors.grey[800]}`,
                                  }}
                                >
                                  <Typography
                                    variant="h6"
                                    sx={{ marginBottom: "10px" }}
                                  >
                                    Available Rollback:
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 2,
                                    }}
                                  >
                                    <Box>
                                      <Typography variant="body1">
                                        안내타입:{" "}
                                        {rollbackTask.is_info_mail
                                          ? "신규문의"
                                          : "재문의"}
                                      </Typography>
                                      <Typography variant="body1">
                                        Completed at:{" "}
                                        {dayjs(
                                          rollbackTask.completed_at
                                        ).format("YYYY-MM-DD HH:mm")}
                                      </Typography>
                                      <Typography variant="body1">
                                        Completed by:{" "}
                                        {rollbackTask.completed_by}
                                      </Typography>
                                    </Box>
                                    <Button
                                      variant="contained"
                                      color="warning"
                                      onClick={async () => {
                                        try {
                                          await Axios.put(
                                            `${process.env.REACT_APP_URL}/api/v1/students/${student.uuid}/memos/${memo.id}/tasks/${rollbackTask.id}/rollback`,
                                            { is_completed: false },
                                            {
                                              headers: {
                                                Authorization: `Bearer ${getCookie('_auth')}`,
                                              },
                                            }
                                          );
                                          setSuccessAlertMessage(
                                            "Task rolled back successfully!"
                                          );
                                          setSuccessAlertOpen(true);
                                          setStudentUpdated(true);
                                          handleDialogMemoClose();
                                        } catch (error) {
                                          setErrorAlertMessage(
                                            error.response?.data?.message ||
                                              "Failed to rollback task"
                                          );
                                          setErrorAlertOpen(true);
                                        }
                                      }}
                                    >
                                      Rollback
                                    </Button>
                                  </Box>
                                </Box>
                              )}
                              {memo.tasks?.length > 0 && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    marginTop: "20px",
                                    padding: "15px",
                                    backgroundColor: colors.primary[900],
                                    borderRadius: "8px",
                                    border: `1px solid ${colors.grey[800]}`,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      color={colors.grey[300]}
                                      sx={{ minWidth: "120px" }}
                                    >
                                      Requested:
                                    </Typography>
                                    <Typography variant="h6">
                                      {dayjs(memo.tasks[0].requested_at)
                                        .tz("America/New_York")
                                        .format("YYYY-MM-DD hh:mm A")}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      color={colors.grey[300]}
                                      sx={{ minWidth: "120px" }}
                                    >
                                      First Reminder:
                                    </Typography>
                                    <Typography
                                      variant="h6"
                                      color={
                                        memo.tasks[0].first_email_sent_at
                                          ? colors.primary[500]
                                          : colors.grey[400]
                                      }
                                      sx={{
                                        fontStyle: memo.tasks[0]
                                          .first_email_sent_at
                                          ? "normal"
                                          : "italic",
                                      }}
                                    >
                                      {memo.tasks[0].first_email_sent_at
                                        ? `Sent at ${dayjs(
                                            memo.tasks[0].first_email_sent_at
                                          )
                                            .tz("America/New_York")
                                            .format(
                                              "YYYY-MM-DD hh:mm A"
                                            )}`
                                        : memo.tasks[0].first_email_scheduled === false
                                          ? "Cancelled"
                                          : `(Scheduled) ${calculateFirstEmailSentAt(
                                              memo.tasks[0].requested_at
                                            )}`}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      color={colors.grey[300]}
                                      sx={{ minWidth: "120px" }}
                                    >
                                      Second Reminder:
                                    </Typography>
                                    <Typography
                                      variant="h6"
                                      color={
                                        memo.tasks[0].second_email_sent_at
                                          ? colors.primary[500]
                                          : colors.grey[400]
                                      }
                                      sx={{
                                        fontStyle: memo.tasks[0]
                                          .second_email_sent_at
                                          ? "normal"
                                          : "italic",
                                      }}
                                    >
                                      {memo.tasks[0].second_email_sent_at
                                        ? `Sent at ${dayjs(
                                            memo.tasks[0].second_email_sent_at
                                          )
                                            .tz("America/New_York")
                                            .format(
                                              "YYYY-MM-DD hh:mm A [EST]"
                                            )}`
                                        : memo.tasks[0].second_email_scheduled === false
                                          ? "Cancelled"
                                          : `(Scheduled) ${calculateSecondEmailSentAt(
                                              memo.tasks[0].requested_at
                                            )}`}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </DialogTitle>
                            <DialogActions
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                width: "100%",
                                padding: "16px",
                                gap: 2,
                                alignItems: "center",
                              }}
                            >
                              {memo.tasks?.length > 0 ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    width: "100%",
                                  }}
                                >
                                  {memo.tasks[0].claimed_by === currentUser ? (
                                    <Button
                                      variant="contained"
                                      fullWidth
                                      onClick={async () => {
                                        try {
                                          await Axios.put(
                                            `${process.env.REACT_APP_URL}/api/v1/students/${student.uuid}/memos/${memo.id}/tasks/${memo.tasks[0].id}/unclaim`,
                                            {},
                                            {
                                              headers: {
                                                Authorization: `Bearer ${getCookie('_auth')}`,
                                              },
                                            }
                                          );
                                          setSuccessAlertMessage(
                                            "Task unassigned successfully!"
                                          );
                                          setSuccessAlertOpen(true);
                                          setStudentUpdated(true);
                                          handleDialogMemoClose();
                                        } catch (error) {
                                          setErrorAlertMessage(
                                            error.response?.data?.message ||
                                              "Failed to unassign task"
                                          );
                                          setErrorAlertOpen(true);
                                        }
                                      }}
                                      color="primary"
                                    >
                                      Unassign
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="contained"
                                      fullWidth
                                      onClick={async () => {
                                        try {
                                          await Axios.put(
                                            `${process.env.REACT_APP_URL}/api/v1/students/${student.uuid}/memos/${memo.id}/tasks/${memo.tasks[0].id}/claim`,
                                            {},
                                            {
                                              headers: {
                                                Authorization: `Bearer ${getCookie('_auth')}`,
                                              },
                                            }
                                          );
                                          setSuccessAlertMessage(
                                            "Task assigned successfully!"
                                          );
                                          setSuccessAlertOpen(true);
                                          setStudentUpdated(true);
                                          handleDialogMemoClose();
                                        } catch (error) {
                                          setErrorAlertMessage(
                                            error.response?.data?.message ||
                                              "Failed to assign task"
                                          );
                                          setErrorAlertOpen(true);
                                        }
                                      }}
                                      disabled={
                                        memo.tasks[0].claimed_by !== null
                                      }
                                      color="primary"
                                    >
                                      Assign to me
                                    </Button>
                                  )}
                                  <Button
                                    onClick={() => setCompleteDialogOpen(true)}
                                    color="success"
                                    variant="contained"
                                    fullWidth
                                  >
                                    Mark as Complete
                                  </Button>
                                  <Button
                                    onClick={handleDialogMemoClose}
                                    color="error"
                                    variant="contained"
                                    fullWidth
                                  >
                                    Cancel
                                  </Button>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    width: "100%",
                                  }}
                                >
                                  <Button
                                    onClick={async () => {
                                      try {
                                        const response = await Axios.get(
                                          `${process.env.REACT_APP_URL}/api/v1/students/${student.uuid}/memos/${memo.id}/tasks/rollback`,
                                          {
                                            headers: {
                                              Authorization: `Bearer ${getCookie('_auth')}`,
                                            },
                                          }
                                        );
                                        if (
                                          response.data.message ===
                                          "No rollback found!"
                                        ) {
                                          setRollbackTask(response.data.task);
                                          setErrorAlertMessage(
                                            "No rollbacks available for this task"
                                          );
                                          setErrorAlertOpen(true);
                                        } else {
                                          setRollbackTask(response.data.task);
                                        }
                                      } catch (error) {
                                        console.log(error);
                                      }
                                    }}
                                    color="warning"
                                    variant="contained"
                                    fullWidth
                                    disabled={rollbackTask !== null}
                                  >
                                    Search for rollbacks
                                  </Button>
                                  <Button
                                    onClick={async () => {
                                      try {
                                        await Axios.post(
                                          `${process.env.REACT_APP_URL}/api/v1/students/${student.uuid}/memos/${memo.id}/tasks`,
                                          {},
                                          {
                                            headers: {
                                              Authorization: `Bearer ${getCookie('_auth')}`,
                                            },
                                          }
                                        );
                                        setSuccessAlertMessage(
                                          "Task created successfully!"
                                        );
                                        setSuccessAlertOpen(true);
                                        setStudentUpdated(true);
                                        setRollbackTask(null);
                                        handleDialogMemoClose();
                                      } catch (error) {
                                        setErrorAlertMessage(
                                          error.response?.data?.message ||
                                            "Failed to create task"
                                        );
                                        setErrorAlertOpen(true);
                                      }
                                    }}
                                    color="success"
                                    variant="contained"
                                    fullWidth
                                  >
                                    Create new task
                                  </Button>

                                  <Button
                                    onClick={handleDialogMemoClose}
                                    color="error"
                                    variant="contained"
                                    fullWidth
                                  >
                                    Cancel
                                  </Button>
                                </Box>
                              )}
                            </DialogActions>
                            <Dialog
                              open={completeDialogOpen}
                              onClose={() => setCompleteDialogOpen(false)}
                              aria-labelledby="complete-dialog-title"
                            >
                              <DialogTitle id="complete-dialog-title">
                                Are you sure you want to mark this task as
                                complete?
                              </DialogTitle>
                              <DialogActions>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: 1,
                                  }}
                                >
                                  <Button
                                    onClick={async () => {
                                      try {
                                        await Axios.put(
                                          `${process.env.REACT_APP_URL}/api/v1/students/${student.uuid}/memos/${memo.id}/tasks/${memo.tasks[0].id}`,
                                          {
                                            is_completed: true,
                                          },
                                          {
                                            headers: {
                                              Authorization: `Bearer ${getCookie('_auth')}`,
                                            },
                                          }
                                        );
                                        setSuccessAlertMessage(
                                          "Task marked as complete!"
                                        );
                                        setSuccessAlertOpen(true);
                                        setStudentUpdated(true);
                                        setCompleteDialogOpen(false);
                                        handleDialogMemoClose();
                                      } catch (error) {
                                        setErrorAlertMessage(
                                          error.response?.data?.message ||
                                            "Failed to complete task"
                                        );
                                        setErrorAlertOpen(true);
                                      }
                                    }}
                                    color="success"
                                  >
                                    Yes
                                  </Button>
                                  <Button
                                    onClick={() => setCompleteDialogOpen(false)}
                                    color="error"
                                  >
                                    No
                                  </Button>
                                </Box>
                              </DialogActions>
                            </Dialog>
                          </Dialog>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            width: "100%",
                            textAlign: "center",
                          }}
                          onClick={() => {
                            if (
                              // modifyingConsulDate ||
                              // modifyingDateEmailSent ||
                              modifyingParentName ||
                              modifyingPhone ||
                              modifyingEmail ||
                              modifyingEmail2 ||
                              modifyingMemo > -1 ||
                              modifyingMemoTitle > -1
                            )
                              return;
                            setModifyingMemoTitle(memo.id);
                          }}
                        >
                          {modifyingMemoTitle === memo.id ? (
                            <input
                              type="text"
                              value={modifiedMemo.get(memo.id)?.title}
                              onChange={(e) => {
                                const newMemo = new Map(modifiedMemo);
                                newMemo.set(memo.id, {
                                  ...memo,
                                  title: e.target.value,
                                });
                                setModifiedMemo(newMemo);
                              }}
                              ref={memoTitleRef}
                              data-key={memo.id}
                              style={{
                                width: "100%",
                                textAlign: "center",
                                padding: "10px 0",
                              }}
                            />
                          ) : (
                            <Typography
                              variant="h6"
                              color="primary"
                              sx={{ padding: "10px 0" }}
                            >
                              {modifiedMemo.get(memo.id)?.title}{" "}
                              {modifiedMemo.get(memo.id)?.date
                                ? `(${modifiedMemo.get(memo.id)?.date})`
                                : ""}
                            </Typography>
                          )}
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            width: "100%",
                            height: "100%",
                            textAlign: "center",
                          }}
                          onClick={() => {
                            if (
                              // modifyingConsulDate ||
                              // modifyingDateEmailSent ||
                              modifyingParentName ||
                              modifyingPhone ||
                              modifyingEmail ||
                              modifyingEmail2 ||
                              modifyingMemo > -1 ||
                              modifyingMemoTitle > -1
                            )
                              return;
                            setModifyingMemo(memo.id);
                          }}
                        >
                          {modifyingMemo === memo.id ? (
                            <Box ref={memoRef} data-key={memo.id} sx={{ width: "100%" }}>
                              <MainMemoOptionsToolbar
                                toolbarRef={memoToolbarRef}
                                colors={colors}
                                isMainMemo={true}
                                inline={true}
                              />
                              <Box
                                component="div"
                                contentEditable
                                suppressContentEditableWarning
                                ref={memoContentRef}
                                sx={{
                                  minHeight: "220px",
                                  width: "100%",
                                  border: "1px solid #ccc",
                                  borderTop: "none",
                                  padding: "8px",
                                  whiteSpace: "pre-wrap",
                                  overflow: "auto",
                                  "&:focus": { outline: "2px solid #599DD5" },
                                }}
                              />
                            </Box>
                          ) : (
                            <MemoContent
                              sx={{ width: "100%" }}
                              html={modifiedMemo.get(memo.id)?.content}
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  }

                  return (
                    moreMemos && (
                      <Box
                        key={memo.id}
                        id={`memo-${memo.id}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          width: "100%",
                          minHeight: "400px",
                          borderTop: `1px solid ${colors.grey[800]}`,
                          textAlign: "center",
                          padding: "20px 0",
                          marginBottom:
                            index === student.memos.length - 1 ? "20px" : "0",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 1,
                            width: "100%",
                            justifyContent: "flex-end",
                            alignItems: "center",
                          }}
                        >
                          {/* a memo written from a phone call opens the call (recording + full transcript) */}
                          <MemoCallButton memo={memo} size={18} />
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBookmarkToggle(memo.id, memo.bookmark);
                            }}
                            sx={{ 
                              padding: '4px',
                              '&:hover': {
                                backgroundColor: 'transparent',
                              }
                            }}
                          >
                            {memo.bookmark ? (
                              <BookmarkIcon
                                fontSize="small"
                                sx={{ 
                                  color: '#ed6c02',
                                  transition: 'color 0.2s ease',
                                  '&:hover': {
                                    color: '#ff8c00'
                                  }
                                }}
                              />
                            ) : (
                              <BookmarkBorderIcon
                                fontSize="small"
                                sx={{ 
                                  color: colors.grey[500],
                                  transition: 'color 0.2s ease',
                                  '&:hover': {
                                    color: colors.grey[300]
                                  }
                                }}
                              />
                            )}
                          </IconButton>
                          <Tooltip
                            title={
                              memo.tasks?.length > 0 && memo.tasks[0].claimed_by
                                ? `Assigned to: ${memo.tasks[0].claimed_by}`
                                : memo.tasks?.length > 0
                                ? "Unassigned"
                                : "No task created"
                            }
                          >
                            <CancelScheduleSendIcon
                              fontSize="small"
                              color={
                                !memo.tasks?.length
                                  ? "disabled"
                                  : memo.tasks.some(
                                      (task) =>
                                        !task.is_completed && task.is_info_mail
                                    )
                                  ? "error"
                                  : memo.tasks.some(
                                      (task) => !task.is_completed
                                    )
                                  ? "warning"
                                  : "disabled"
                              }
                              sx={{ cursor: "pointer" }}
                              onClick={() => handleClickDialogMemoOpen(memo.id)}
                            />
                          </Tooltip>
                          <Dialog
                            open={dialogMemoId === memo.id}
                            onClose={handleDialogMemoClose}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                            PaperProps={{
                              style: {
                                minWidth: "500px",
                                padding: "20px",
                              },
                            }}
                          >
                            <DialogTitle id="alert-dialog-title">
                              <Typography
                                variant="h5"
                                color="primary"
                                sx={{
                                  marginBottom: "15px",
                                  fontWeight: "bold",
                                }}
                              >
                                {memo.tasks?.length > 0
                                  ? `(${
                                      memo.tasks[0].is_info_mail
                                        ? "신규문의"
                                        : "재문의"
                                    }) This memo is currently registered as a task.`
                                  : "Would you like to create a new task for this memo?"}
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontStyle: "italic",
                                  opacity: 0.9,
                                }}
                              >
                                {memo.tasks?.length > 0 &&
                                  (memo.tasks[0].claimed_by
                                    ? `Assigned to: ${memo.tasks[0].claimed_by}`
                                    : `Assigned to: N/A`)}
                              </Typography>
                              <Divider />
                              {rollbackTask && (
                                <Box
                                  sx={{
                                    marginTop: "20px",
                                    padding: "15px",
                                    backgroundColor: colors.primary[900],
                                    borderRadius: "8px",
                                    border: `1px solid ${colors.grey[800]}`,
                                  }}
                                >
                                  <Typography
                                    variant="h6"
                                    sx={{ marginBottom: "10px" }}
                                  >
                                    Available Rollback:
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 2,
                                    }}
                                  >
                                    <Box>
                                      <Typography variant="body1">
                                        Task ID: {rollbackTask.id}
                                      </Typography>
                                      <Typography variant="body1">
                                        Completed at:{" "}
                                        {dayjs(
                                          rollbackTask.completed_at
                                        ).format("YYYY-MM-DD HH:mm")}
                                      </Typography>
                                      <Typography variant="body1">
                                        Completed by:{" "}
                                        {rollbackTask.completed_by}
                                      </Typography>
                                    </Box>
                                    <Button
                                      variant="contained"
                                      color="warning"
                                      onClick={async () => {
                                        try {
                                          await Axios.put(
                                            `${process.env.REACT_APP_URL}/api/v1/students/${student.uuid}/memos/${memo.id}/tasks/${rollbackTask.id}/rollback`,
                                            { is_completed: false },
                                            {
                                              headers: {
                                                Authorization: `Bearer ${getCookie('_auth')}`,
                                              },
                                            }
                                          );
                                          setSuccessAlertMessage(
                                            "Task rolled back successfully!"
                                          );
                                          setSuccessAlertOpen(true);
                                          setStudentUpdated(true);
                                          handleDialogMemoClose();
                                        } catch (error) {
                                          setErrorAlertMessage(
                                            error.response?.data?.message ||
                                              "Failed to rollback task"
                                          );
                                          setErrorAlertOpen(true);
                                        }
                                      }}
                                    >
                                      Rollback
                                    </Button>
                                  </Box>
                                </Box>
                              )}
                              {memo.tasks?.length > 0 && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    marginTop: "20px",
                                    padding: "15px",
                                    backgroundColor: colors.primary[900],
                                    borderRadius: "8px",
                                    border: `1px solid ${colors.grey[800]}`,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      color={colors.grey[300]}
                                      sx={{ minWidth: "120px" }}
                                    >
                                      Requested:
                                    </Typography>
                                    <Typography variant="h6">
                                      {dayjs(memo.tasks[0].requested_at)
                                        .tz("America/New_York")
                                        .format("YYYY-MM-DD hh:mm A")}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      color={colors.grey[300]}
                                      sx={{ minWidth: "120px" }}
                                    >
                                      First Reminder:
                                    </Typography>
                                    <Typography
                                      variant="h6"
                                      color={
                                        memo.tasks[0].first_email_sent_at
                                          ? colors.primary[500]
                                          : colors.grey[400]
                                      }
                                      sx={{
                                        fontStyle: memo.tasks[0]
                                          .first_email_sent_at
                                          ? "normal"
                                          : "italic",
                                      }}
                                    >
                                      {memo.tasks[0].first_email_sent_at
                                        ? `Sent at ${dayjs(
                                            memo.tasks[0].first_email_sent_at
                                          )
                                            .tz("America/New_York")
                                            .format(
                                              "YYYY-MM-DD hh:mm A"
                                            )}`
                                        : memo.tasks[0].first_email_scheduled === false
                                          ? "Cancelled"
                                          : `(Scheduled) ${calculateFirstEmailSentAt(
                                              memo.tasks[0].requested_at
                                            )}`}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      color={colors.grey[300]}
                                      sx={{ minWidth: "120px" }}
                                    >
                                      Second Reminder:
                                    </Typography>
                                    <Typography
                                      variant="h6"
                                      color={
                                        memo.tasks[0].second_email_sent_at
                                          ? colors.primary[500]
                                          : colors.grey[400]
                                      }
                                      sx={{
                                        fontStyle: memo.tasks[0]
                                          .second_email_sent_at
                                          ? "normal"
                                          : "italic",
                                      }}
                                    >
                                      {memo.tasks[0].second_email_sent_at
                                        ? `Sent at ${dayjs(
                                            memo.tasks[0].second_email_sent_at
                                          )
                                            .tz("America/New_York")
                                            .format(
                                              "YYYY-MM-DD hh:mm A"
                                            )}`
                                        : memo.tasks[0].second_email_scheduled === false
                                          ? "Cancelled"
                                          : `(Scheduled) ${calculateSecondEmailSentAt(
                                              memo.tasks[0].requested_at
                                            )}`}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </DialogTitle>
                            <DialogActions
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                width: "100%",
                                padding: "16px",
                                gap: 2,
                                alignItems: "center",
                              }}
                            >
                              {memo.tasks?.length > 0 ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    width: "100%",
                                  }}
                                >
                                  {memo.tasks[0].claimed_by === currentUser ? (
                                    <Button
                                      variant="contained"
                                      fullWidth
                                      onClick={async () => {
                                        try {
                                          await Axios.put(
                                            `${process.env.REACT_APP_URL}/api/v1/students/${student.uuid}/memos/${memo.id}/tasks/${memo.tasks[0].id}/unclaim`,
                                            {},
                                            {
                                              headers: {
                                                Authorization: `Bearer ${getCookie('_auth')}`,
                                              },
                                            }
                                          );
                                          setSuccessAlertMessage(
                                            "Task unassigned successfully!"
                                          );
                                          setSuccessAlertOpen(true);
                                          setStudentUpdated(true);
                                          handleDialogMemoClose();
                                        } catch (error) {
                                          setErrorAlertMessage(
                                            error.response?.data?.message ||
                                              "Failed to unassign task"
                                          );
                                          setErrorAlertOpen(true);
                                        }
                                      }}
                                      color="primary"
                                    >
                                      Unassign
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="contained"
                                      fullWidth
                                      onClick={async () => {
                                        try {
                                          await Axios.put(
                                            `${process.env.REACT_APP_URL}/api/v1/students/${student.uuid}/memos/${memo.id}/tasks/${memo.tasks[0].id}/claim`,
                                            {},
                                            {
                                              headers: {
                                                Authorization: `Bearer ${getCookie('_auth')}`,
                                              },
                                            }
                                          );
                                          setSuccessAlertMessage(
                                            "Task assigned successfully!"
                                          );
                                          setSuccessAlertOpen(true);
                                          setStudentUpdated(true);
                                          handleDialogMemoClose();
                                        } catch (error) {
                                          setErrorAlertMessage(
                                            error.response?.data?.message ||
                                              "Failed to assign task"
                                          );
                                          setErrorAlertOpen(true);
                                        }
                                      }}
                                      disabled={memo.tasks[0].claimed_by !== null}
                                      color="primary"
                                    >
                                      Assign to me
                                    </Button>
                                  )}
                                  <Button
                                    onClick={() => setCompleteDialogOpen(true)}
                                    color="success"
                                    variant="contained"
                                    fullWidth
                                  >
                                    Mark as Complete
                                  </Button>
                                  <Button
                                    onClick={handleDialogMemoClose}
                                    color="error"
                                    variant="contained"
                                    fullWidth
                                  >
                                    Cancel
                                  </Button>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    width: "100%",
                                  }}
                                >
                                  <Button
                                    onClick={async () => {
                                      try {
                                        const response = await Axios.get(
                                          `${process.env.REACT_APP_URL}/api/v1/students/${student.uuid}/memos/${memo.id}/tasks/rollback`,
                                          {
                                            headers: {
                                              Authorization: `Bearer ${getCookie('_auth')}`,
                                            },
                                          }
                                        );
                                        if (
                                          response.data.message ===
                                          "No rollback found!"
                                        ) {
                                          setRollbackTask(response.data.task);
                                          setErrorAlertMessage(
                                            "No rollbacks available for this task"
                                          );
                                          setErrorAlertOpen(true);
                                        } else {
                                          setRollbackTask(response.data.task);
                                        }
                                      } catch (error) {
                                        console.log(error);
                                      }
                                    }}
                                    color="warning"
                                    variant="contained"
                                    fullWidth
                                    disabled={rollbackTask !== null}
                                  >
                                    Search for rollbacks
                                  </Button>
                                  <Button
                                    onClick={async () => {
                                      try {
                                        await Axios.post(
                                          `${process.env.REACT_APP_URL}/api/v1/students/${student.uuid}/memos/${memo.id}/tasks`,
                                          {},
                                          {
                                            headers: {
                                              Authorization: `Bearer ${getCookie('_auth')}`,
                                            },
                                          }
                                        );
                                        setSuccessAlertMessage(
                                          "Task created successfully!"
                                        );
                                        setSuccessAlertOpen(true);
                                        setStudentUpdated(true);
                                        setRollbackTask(null);
                                        handleDialogMemoClose();
                                      } catch (error) {
                                        setErrorAlertMessage(
                                          error.response?.data?.message ||
                                            "Failed to create task"
                                        );
                                        setErrorAlertOpen(true);
                                      }
                                    }}
                                    color="success"
                                    variant="contained"
                                    fullWidth
                                  >
                                    Create new task
                                  </Button>

                                  <Button
                                    onClick={handleDialogMemoClose}
                                    color="error"
                                    variant="contained"
                                    fullWidth
                                  >
                                    Cancel
                                  </Button>
                                </Box>
                              )}
                            </DialogActions>
                            <Dialog
                              open={completeDialogOpen}
                              onClose={() => setCompleteDialogOpen(false)}
                              aria-labelledby="complete-dialog-title"
                            >
                              <DialogTitle id="complete-dialog-title">
                                Are you sure you want to mark this task as
                                complete?
                              </DialogTitle>
                              <DialogActions>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: 1,
                                  }}
                                >
                                  <Button
                                    onClick={async () => {
                                      try {
                                        await Axios.put(
                                          `${process.env.REACT_APP_URL}/api/v1/students/${student.uuid}/memos/${memo.id}/tasks/${memo.tasks[0].id}`,
                                          {
                                            is_completed: true,
                                          },
                                          {
                                            headers: {
                                              Authorization: `Bearer ${getCookie('_auth')}`,
                                            },
                                          }
                                        );
                                        setSuccessAlertMessage(
                                          "Task marked as complete!"
                                        );
                                        setSuccessAlertOpen(true);
                                        setStudentUpdated(true);
                                        setCompleteDialogOpen(false);
                                        handleDialogMemoClose();
                                      } catch (error) {
                                        setErrorAlertMessage(
                                          error.response?.data?.message ||
                                            "Failed to complete task"
                                        );
                                        setErrorAlertOpen(true);
                                      }
                                    }}
                                    color="success"
                                  >
                                    Yes
                                  </Button>
                                  <Button
                                    onClick={() => setCompleteDialogOpen(false)}
                                    color="error"
                                  >
                                    No
                                  </Button>
                                </Box>
                              </DialogActions>
                            </Dialog>
                          </Dialog>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            width: "100%",
                            textAlign: "center",
                          }}
                          onClick={() => {
                            if (
                              // modifyingConsulDate ||
                              // modifyingDateEmailSent ||
                              modifyingParentName ||
                              modifyingPhone ||
                              modifyingEmail ||
                              modifyingEmail2 ||
                              modifyingMemo > -1 ||
                              modifyingMemoTitle > -1
                            )
                              return;
                            setModifyingMemoTitle(memo.id);
                          }}
                        >
                          {modifyingMemoTitle === memo.id ? (
                            <input
                              type="text"
                              value={modifiedMemo.get(memo.id)?.title}
                              onChange={(e) => {
                                const newMemo = new Map(modifiedMemo);
                                newMemo.set(memo.id, {
                                  ...memo,
                                  title: e.target.value,
                                });
                                setModifiedMemo(newMemo);
                              }}
                              ref={memoTitleRef}
                              data-key={memo.id}
                              style={{
                                width: "100%",
                                textAlign: "center",
                                padding: "10px 0",
                              }}
                            />
                          ) : (
                            <Typography
                              variant="h6"
                              color="primary"
                              sx={{ padding: "10px 0" }}
                            >
                              {modifiedMemo.get(memo.id)?.title}{" "}
                              {modifiedMemo.get(memo.id)?.date
                                ? `(${modifiedMemo.get(memo.id)?.date})`
                                : ""}
                            </Typography>
                          )}
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            width: "100%",
                            height: "100%",
                            textAlign: "center",
                          }}
                          onClick={() => {
                            if (
                              // modifyingConsulDate ||
                              // modifyingDateEmailSent ||
                              modifyingParentName ||
                              modifyingPhone ||
                              modifyingEmail ||
                              modifyingEmail2 ||
                              modifyingMemo > -1 ||
                              modifyingMemoTitle > -1
                            )
                              return;
                            setModifyingMemo(memo.id);
                          }}
                        >
                          {modifyingMemo === memo.id ? (
                            <Box ref={memoRef} data-key={memo.id} sx={{ width: "100%" }}>
                              <MainMemoOptionsToolbar
                                toolbarRef={memoToolbarRef}
                                colors={colors}
                                isMainMemo={true}
                                inline={true}
                              />
                              <Box
                                component="div"
                                contentEditable
                                suppressContentEditableWarning
                                ref={memoContentRef}
                                sx={{
                                  minHeight: "220px",
                                  width: "100%",
                                  border: "1px solid #ccc",
                                  borderTop: "none",
                                  padding: "8px",
                                  whiteSpace: "pre-wrap",
                                  overflow: "auto",
                                  "&:focus": { outline: "2px solid #599DD5" },
                                }}
                              />
                            </Box>
                          ) : (
                            <MemoContent
                              sx={{ width: "100%" }}
                              html={modifiedMemo.get(memo.id)?.content}
                            />
                          )}
                        </Box>
                      </Box>
                    )
                  );
                });
              })()}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  width: "100%",
                  textAlign: "center",
                  marginTop: "20px",
                  marginBottom: "60px",
                }}
              >
                <input
                  type="text"
                  value={newMemoTitle}
                  onChange={(e) => setNewMemoTitle(e.target.value)}
                  placeholder="Title"
                  style={{
                    width: "100%",
                    textAlign: "center",
                    padding: "10px 0",
                  }}
                />
                <Box
                  sx={{ width: "100%", marginBottom: "10px" }}
                  // Hide the toolbar once focus leaves the editor+toolbar region.
                  // relatedTarget is where focus is going; if it's still inside this
                  // wrapper (e.g. a toolbar button/select), keep the toolbar visible.
                  onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                      setIsAddingNewMemo(false);
                    }
                  }}
                >
                  {isAddingNewMemo && (
                    <Box
                      // Keep the editor focused (and its selection intact) when clicking
                      // toolbar controls, so the toolbar doesn't blur-hide — including in
                      // Safari/Firefox where buttons don't take focus on click. Native
                      // controls (font-size <select>, color <input>) still need mousedown.
                      onMouseDown={(e) => {
                        const tag = e.target.tagName;
                        if (tag !== "SELECT" && tag !== "OPTION" && tag !== "INPUT") {
                          e.preventDefault();
                        }
                      }}
                    >
                      <MainMemoOptionsToolbar
                        toolbarRef={newMemoToolbarRef}
                        colors={colors}
                        isMainMemo={true}
                        inline={true}
                      />
                    </Box>
                  )}
                  <Box
                    component="div"
                    contentEditable
                    suppressContentEditableWarning
                    ref={newMemoContentRef}
                    onFocus={() => setIsAddingNewMemo(true)}
                    data-placeholder="Content"
                    sx={{
                      minHeight: "220px",
                      width: "100%",
                      border: "1px solid #ccc",
                      borderTop: isAddingNewMemo ? "none" : "1px solid #ccc",
                      padding: "8px",
                      whiteSpace: "pre-wrap",
                      overflow: "auto",
                      textAlign: "left",
                      "&:focus": { outline: "2px solid #599DD5" },
                      "&:empty:before": {
                        content: "attr(data-placeholder)",
                        color: "#999",
                      },
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AddMemoDatePicker
                    reminderDate={newMemoReminder}
                    setReminderDate={setNewMemoReminder}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={newMemoBookmark}
                        onChange={(e) => setNewMemoBookmark(e.target.checked)}
                        icon={<BookmarkBorderIcon />}
                        checkedIcon={<BookmarkIcon />}
                        sx={{
                          color: colors.grey[400],
                          '&.Mui-checked': {
                            color: '#ed6c02',
                          },
                        }}
                      />
                    }
                    label="Bookmark"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        color: 'primary.main',
                        fontSize: '14px',
                      },
                    }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Button
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "40px",
                    textAlign: "center",
                    padding: "10px 20px",
                    marginBottom: "10px",
                    backgroundColor: colors.greenAccent[700],
                    whiteSpace: "nowrap",
                  }}
                  onClick={(e) => uploadNewMemo(e)}
                >
                  <Typography variant="h6" color={colors.primary[400]} sx={{ whiteSpace: "nowrap" }}>
                    Add Memo
                  </Typography>
                </Button>
                {student.memos?.length > 0 && (
                  <Tooltip 
                    title={showBookmarkedOnly 
                      ? "Click to show all memos" 
                      : "Click to show only bookmarked memos"
                    }
                  >
                    <Button
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                        minHeight: "40px",
                        textAlign: "center",
                        padding: "10px 20px",
                        marginBottom: "10px",
                        backgroundColor: showBookmarkedOnly 
                          ? '#ed6c02' 
                          : colors.greenAccent[600],
                        transition: "background-color 0.3s ease",
                        whiteSpace: "nowrap",
                        "&:hover": {
                          backgroundColor: showBookmarkedOnly 
                            ? '#e65100' 
                            : colors.greenAccent[700],
                        }
                      }}
                      onClick={() => setShowBookmarkedOnly(!showBookmarkedOnly)}
                    >
                      {showBookmarkedOnly ? (
                        <BookmarkIcon fontSize="small" />
                      ) : (
                        <BookmarkBorderIcon fontSize="small" />
                      )}
                      <Typography variant="h6" color={colors.primary[400]} sx={{ whiteSpace: "nowrap" }}>
                        {showBookmarkedOnly ? "Show All" : "Bookmarked"}
                      </Typography>
                    </Button>
                  </Tooltip>
                )}
                {(student.memos?.length || 0) > 1 && (
                  <Button
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "40px",
                      textAlign: "center",
                      padding: "10px 20px",
                      marginBottom: "10px",
                      backgroundColor: colors.greenAccent[500],
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => setMoreMemos(!moreMemos)}
                  >
                    <Typography variant="h6" color={colors.primary[400]} sx={{ whiteSpace: "nowrap" }}>
                      {moreMemos ? "Less" : "More"}
                    </Typography>
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
      <Alert
        successAlertOpen={successAlertOpen}
        setSuccessAlertOpen={setSuccessAlertOpen}
        errorAlertOpen={errorAlertOpen}
        setErrorAlertOpen={setErrorAlertOpen}
        successMsg={successAlertMessage}
        errorMsg={errorAlertMessage}
        vertical="bottom"
        horizontal="left"
      />
    </motion.div>
  );
}
